import { useQuery } from "@apollo/client";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import { SpeciesSelectElement_SpeciesGetByIdsDocument } from "./queries.generated";
import { SpeciesOption, SpeciesSelectMultipleProps } from "./types";
import { useSpeciesSearch } from "./useSpeciesSearch";

export default function SpeciesSelectMultiple({
    value,
    onChange,
    disabled,
    label,
    excludeAssignedToResearchTypeId,
    error,
    helperText,
}: SpeciesSelectMultipleProps) {
    const { t } = useTranslation("shared");

    const {
        isOpen,
        setOpen,
        inputText,
        setInputText,
        options,
        loading: isSearchLoading,
    } = useSpeciesSearch({ excludeAssignedToResearchTypeId });

    // Fetch species by IDs when values are set
    const { data: dataValues, loading: isLoadingValues } = useQuery(SpeciesSelectElement_SpeciesGetByIdsDocument, {
        variables: { ids: value },
        skip: !Array.isArray(value) || value.length < 1,
    });

    const selectedOptionsRaw = dataValues?.settingsSpeciesGetByIds ?? [];
    const selectedOptions = selectedOptionsRaw.filter(Boolean);
    const loading = isSearchLoading || isLoadingValues;

    return (
        <Autocomplete
            multiple
            noOptionsText={t("SpeciesSelectElement.noOptionsFoundLabel")}
            disabled={disabled}
            disableClearable
            filterOptions={x => x}
            getOptionLabel={option => option.name}
            getOptionKey={option => option.id}
            isOptionEqualToValue={(option, selectedOption) => option.id === selectedOption.id}
            loading={loading}
            inputValue={inputText}
            onOpen={() => setOpen(true)}
            onClose={() => {
                setOpen(false);
                setInputText("");
            }}
            onInputChange={(_, nextValue, reason) => {
                if (reason === "input") {
                    setInputText(nextValue);
                }
                if (reason === "reset" && !isOpen) {
                    setInputText(nextValue);
                }
            }}
            open={isOpen}
            options={options}
            onChange={(_, nextValue) => {
                const values = nextValue as SpeciesOption[];
                onChange(values.map(v => v.id));
            }}
            value={selectedOptions}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    error={error}
                    helperText={helperText}
                    placeholder={value.length === 0 ? t("SpeciesSelectElement.typeToSearchLabel") : undefined}
                />
            )}
        />
    );
}
